import ProTable from "@ant-design/pro-table";
import styled from "styled-components";
import { colors } from "src/theme/colors";
import { line, radii } from "src/theme/ui";
import { tableChrome, tablePagination } from "src/components/ui/tableChrome";

/**
 * Every ProTable in the app, in the product's own table.
 *
 * It used to be this file's job to recolour one checkbox, and everything else
 * came out as Ant Design draws it: ruled cells, the library's grey headings,
 * square hover. The dashboard's own table had the product's treatment written
 * INSIDE it, so it was the only table in the app that looked like the
 * product. That treatment now lives in `ui/tableChrome`, and this is where
 * the rest of the tables put it on.
 */
export const ProTableTheme = styled(ProTable)`
  ${tableChrome(16)}
  ${tablePagination}

  /* Pro-table wraps its table in a Card whose body carries 24px of side
     padding, on top of the wrapper's own — so a table inside a dialog stood
     forty-odd pixels in from the dialog's text and read as a floating object
     rather than as the dialog's content. The card is scaffolding; it should
     not be visible at all. */
  > .ant-card,
  .ant-pro-card {
    background: transparent;
    box-shadow: none;
  }

  > .ant-card > .ant-card-body,
  .ant-pro-card > .ant-pro-card-body {
    padding: 0;
  }

  /* Ant Design's own size rule is as specific as the shared one and lands
     after it, so the row padding has to be stated again here to win. */
  && .ant-table.ant-table-middle .ant-table-thead > tr > th,
  && .ant-table.ant-table-small .ant-table-thead > tr > th {
    padding: 8px 12px;
  }

  && .ant-table.ant-table-middle .ant-table-tbody > tr > td,
  && .ant-table.ant-table-small .ant-table-tbody > tr > td {
    padding: 9px 12px;
  }

  && .ant-table-thead > tr > th:first-child,
  && .ant-table-tbody > tr > td:first-child {
    padding-left: 16px;
  }

  && .ant-table-thead > tr > th:last-child,
  && .ant-table-tbody > tr > td:last-child {
    padding-right: 16px;
  }

  .ant-checkbox-inner {
    border-radius: 4px;
    border-color: ${line.field};
  }

  .ant-checkbox-checked .ant-checkbox-inner,
  .ant-checkbox-indeterminate .ant-checkbox-inner::after {
    background-color: ${colors.primary[500]};
    border-color: ${colors.primary[500]};
  }

  .ant-checkbox-wrapper:hover .ant-checkbox-inner,
  .ant-checkbox:hover .ant-checkbox-inner {
    border-color: ${colors.primary[400]};
  }

  /* A selected row stays marked while the eye is elsewhere in the table —
     the hover tint alone said nothing once the pointer moved on. */
  .ant-table-tbody > tr.ant-table-row-selected > td {
    background: ${colors.primary[10]};
  }

  .ant-table-tbody > tr.ant-table-row-selected:hover > td {
    background: ${colors.primary[15]};
  }

  /* The table's own frame, rather than a border on every cell. */
  .ant-table-container {
    border: 1px solid ${line.hairline};
    border-radius: ${radii.lg}px;
    overflow: hidden;
  }

  .ant-table-cell-fix-left,
  .ant-table-cell-fix-right {
    background: ${colors.white};
  }
`;
